import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { Form } from "../../../components/ui/form";
import { Button } from "../../../components/ui/button";
import { FormInput } from "../../../components/form-components/form-input.components";
import { FormSelect } from "../../../components/form-components/form-select.components";
import { useToast } from "../../../hooks/use-toast";
import { useCreateAssetCommonHttp } from "../../../hooks/http/common/utils/asset/create-asset.common.http";
import {
  useCreateCarouselItemAdminHttp,
  useGetCarouselItemByIdAdminHttp,
  useUpdateCarouselItemAdminHttp,
  usePublishCarouselItemAdminHttp,
  type ICreateBody,
} from "../../../hooks/http/admin/carousel/carousel-mutations.admin.http";
import {
  CarouselActionType,
  NavigationTarget,
  CarouselItemStatus,
} from "../../../types/models/carousel.models.types";
import type { ICampaign } from "../../../types/models/campaign.models.types";
import Spinner from "../../../components/spinner.components";
import {
  handleHttpError,
  debounce,
  findErrorMessage,
} from "../../../utils/utils";
import { Uploader } from "../../../components/custom/uploader";
import { ImageIcon } from "lucide-react";
import { useSearchCampaignsAdminHttp } from "../../../hooks/http/admin/campaigns/search-campaigns.admin.http";
import { useGetCampaignByIdAdminHttp } from "../../../hooks/http/admin/campaigns/get-campaign-by-id.admin.http";
import { FormSearch } from "../../../components/form-components/form-api-search.components";
import DeleteIcon from "../../../assets/svgs/common/delete.svg";
import StarIcon from "../../../assets/svgs/common/star.svg";

const FormSchema = z
  .object({
    actionType: z.nativeEnum(CarouselActionType),
    url: z.string().optional(),
    navigationTarget: z.nativeEnum(NavigationTarget).optional(),
    campaignId: z.string().optional(),
    imageFile: z.instanceof(File).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.actionType === CarouselActionType.LINK && !data.url?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["url"],
        message: "URL is required for link actions",
      });
    }
    if (
      data.actionType === CarouselActionType.NAVIGATION &&
      !data.navigationTarget
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["navigationTarget"],
        message: "Navigation target is required",
      });
    }
    if (
      data.actionType === CarouselActionType.NAVIGATION &&
      data.navigationTarget === NavigationTarget.CAMPAIGN_SCREEN &&
      !data.campaignId?.trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["campaignId"],
        message: "Please select a campaign",
      });
    }
  });

type IFormData = z.infer<typeof FormSchema>;

interface Props {
  mode: "create" | "edit";
}

const actionTypeOptions = [
  { label: "Link (external URL)", value: CarouselActionType.LINK },
  { label: "Navigation (in-app)", value: CarouselActionType.NAVIGATION },
];

const navigationTargetOptions = [
  { label: "Campaign Screen", value: NavigationTarget.CAMPAIGN_SCREEN },
  { label: "Payouts Screen", value: NavigationTarget.PAYOUTS_SCREEN },
  { label: "Refer & Earn Screen", value: NavigationTarget.REFER_AND_EARN_SCREEN },
];

export const CarouselItemFormPartial: React.FunctionComponent<Props> = ({
  mode,
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<ICampaign[]>([]);
  const [searchingCampaigns, setSearchingCampaigns] = useState(false);
  const [itemStatus, setItemStatus] = useState<CarouselItemStatus | null>(null);

  const { execute: createCarouselItem, fetching: creating } =
    useCreateCarouselItemAdminHttp();
  const { execute: getCarouselItemById, fetching: loadingItem } =
    useGetCarouselItemByIdAdminHttp();
  const { execute: updateCarouselItem, fetching: updating } =
    useUpdateCarouselItemAdminHttp();
  const { execute: uploadAsset, fetching: uploadingAsset } =
    useCreateAssetCommonHttp();
  const { execute: searchCampaigns } = useSearchCampaignsAdminHttp();
  const { execute: getCampaignById } = useGetCampaignByIdAdminHttp();
  const { execute: publishCarouselItem, fetching: publishing } =
    usePublishCarouselItemAdminHttp();

  const form = useForm<IFormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      actionType: CarouselActionType.LINK,
      url: "",
      navigationTarget: undefined,
      campaignId: "",
    },
  });

  const watchedActionType = form.watch("actionType");
  const watchedNavigationTarget = form.watch("navigationTarget");
  const prevActionTypeRef = useRef<CarouselActionType>(watchedActionType);
  const prevNavigationTargetRef = useRef<NavigationTarget | undefined>(
    watchedNavigationTarget,
  );

  // Reset other fields on actionType change (except imageFile)
  useEffect(() => {
    if (prevActionTypeRef.current !== watchedActionType) {
      if (watchedActionType === CarouselActionType.LINK) {
        form.setValue("navigationTarget", undefined, { shouldDirty: true });
        form.setValue("campaignId", "", { shouldDirty: true });
      } else if (watchedActionType === CarouselActionType.NAVIGATION) {
        form.setValue("url", "", { shouldDirty: true });
      }
      prevActionTypeRef.current = watchedActionType;
    }
  }, [watchedActionType, form]);

  // Reset campaignId when navigationTarget changes
  useEffect(() => {
    if (prevNavigationTargetRef.current !== watchedNavigationTarget) {
      form.setValue("campaignId", "", { shouldDirty: true });
      prevNavigationTargetRef.current = watchedNavigationTarget;
    }
  }, [watchedNavigationTarget, form]);

  // toast validation errors
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  useEffect(() => {
    if (form.formState.errors) {
      const key = Object.keys(form.formState.errors)[0];
      if (key) {
        const error =
          form.formState.errors[key as keyof typeof form.formState.errors];
        const message = findErrorMessage(error) || "An error occurred";
        toast({
          content: message,
          toastType: "error",
        });
      }
    }
  }, [form.formState.errors]);

  // Debounced search for campaigns
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  const debouncedSearchCampaigns = useCallback(
    debounce(async (query: string) => {
      if (!query || !query.trim()) {
        return;
      }
      setSearchingCampaigns(true);
      const response = await searchCampaigns({
        body: null,
        queryParams: { query: query, status: "live" },
      });
      if (response.success) {
        setCampaigns((prev) => {
          const ids = new Set(prev.map((c) => c.id));
          const merged = [...prev];
          for (const camp of response.data.campaigns) {
            if (!ids.has(camp.id)) {
              merged.push(camp);
            }
          }
          return merged;
        });
      }
      setSearchingCampaigns(false);
    }, 300),
    [searchCampaigns],
  );

  // Load existing item in edit mode
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  useEffect(() => {
    if (mode === "edit" && id) {
      (async () => {
        const response = await getCarouselItemById({
          body: null,
          pathParams: { id },
        });
        if (!response.success) return handleHttpError(response, toast);

        const item = response.data.carouselItem;
        const cid =
          (item.action.navigation?.extras?.campaignId as string) ?? "";
        form.reset({
          actionType: item.action.type as CarouselActionType,
          url: item.action.url ?? "",
          navigationTarget: item.action.navigation?.target as
            | NavigationTarget
            | undefined,
          campaignId: cid,
        });
        prevActionTypeRef.current = item.action.type as CarouselActionType;
        prevNavigationTargetRef.current = item.action.navigation?.target as
          | NavigationTarget
          | undefined;
        if (item.asset?.url) {
          setImagePreview(item.asset.url);
        }
        setItemStatus(item.status.status as CarouselItemStatus);

        if (cid) {
          const campRes = await getCampaignById({
            body: null,
            pathParams: { id: cid },
          });
          if (campRes.success) {
            const camp = campRes.data.campaign;
            setCampaigns((prev) => {
              if (prev.some((c) => c.id === camp.id)) return prev;
              return [camp, ...prev];
            });
          }
        }
      })();
    }
  }, [mode, id]);

  function handleImageSelect(files: File[]) {
    if (!files.length) return;
    const file = files[0];
    form.setValue("imageFile", file, { shouldDirty: true });
    setImagePreview(URL.createObjectURL(file));
  }

  function handleImageRemove() {
    form.setValue("imageFile", undefined, { shouldDirty: true });
    setImagePreview(null);
  }

  async function onSubmit(data: IFormData) {
    // Upload asset first if a new image was selected
    let assetId: string | undefined;
    if (data.imageFile) {
      const fd = new FormData();
      fd.append("file", data.imageFile);
      const uploadRes = await uploadAsset({ body: fd });
      if (!uploadRes.success) {
        return toast({
          content: "Image upload failed. Try again.",
          toastType: "error",
        });
      }
      assetId = uploadRes.data.asset.id;
    }

    // Build the action object
    const action: ICreateBody["action"] = {
      type: data.actionType,
      url: data.actionType === CarouselActionType.LINK ? data.url : null,
      navigation:
        data.actionType === CarouselActionType.NAVIGATION &&
        data.navigationTarget
          ? {
              target: data.navigationTarget,
              extras:
                data.navigationTarget === NavigationTarget.CAMPAIGN_SCREEN &&
                data.campaignId
                  ? { campaignId: data.campaignId }
                  : null,
            }
          : null,
    };

    if (mode === "create") {
      if (!assetId) {
        return toast({
          content: "Please select an image or gif.",
          toastType: "error",
        });
      }

      const response = await createCarouselItem({
        body: { action: action, assetId },
      });
      if (!response.success) return handleHttpError(response, toast);

      toast({
        content: "Carousel item created successfully!",
        toastType: "success",
      });
      navigate(`/admin/carousels/${response.data.carouselItem.id}/edit`);
    } else {
      // Edit mode
      const body: Partial<ICreateBody> = {
        action,
        ...(assetId ? { assetId } : {}),
      };

      const response = await updateCarouselItem({
        body,
        pathParams: { id: id! },
      });
      if (!response.success) return handleHttpError(response, toast);

      toast({ content: "Carousel item updated!", toastType: "success" });
      form.reset({}, { keepValues: true });
    }
  }

  const isBusy =
    creating || updating || uploadingAsset || loadingItem || publishing;

  return (
    <div className="flex flex-col flex-1 gap-8 p-8 ">
      <div className="flex flex-col gap-2 px-5">
        <span className="font-semibold text-2xl flex flex-row items-center justify-between">
          {mode === "create" ? "Create carousel item" : "Edit carousel item"}
          <div className="flex gap-4">
            {mode === "edit" && itemStatus === CarouselItemStatus.DRAFT && (
              <Button
                disabled={isBusy || form.formState.isDirty}
                onClick={async () => {
                  const response = await publishCarouselItem({
                    body: null,
                    pathParams: { id: id! },
                  });
                  if (!response.success)
                    return handleHttpError(response, toast);
                  toast({
                    content: "Carousel item is now live!",
                    toastType: "success",
                  });
                  navigate("/admin/carousels");
                }}
                type="button"
                className="w-48 gap-2 bg-primarybuttonbg py-5 font-semibold text-primarybuttontext text-base mt-2"
              >
                {publishing ? <Spinner width={20} /> : "Make live →"}
              </Button>
            )}
            <Button
              disabled={isBusy || (mode === "edit" && !form.formState.isDirty)}
              onClick={() => {
                form.handleSubmit(onSubmit)();
              }}
              type="submit"
              className="w-48 gap-2 bg-primarybuttonbg py-5 font-semibold text-primarybuttontext text-base mt-2"
            >
              {isBusy ? (
                <Spinner width={20} />
              ) : mode === "create" ? (
                "Create →"
              ) : (
                "Save changes →"
              )}
            </Button>
          </div>
        </span>

        {loadingItem && mode === "edit" ? (
          <div className="flex justify-center items-center py-16">
            <Spinner />
          </div>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="grid grid-cols-2 gap-6 mt-4"
            >
              {/* Image upload with preview */}
              <div className="flex flex-col gap-2">
                <span className="font-semibold flex flex-row items-center gap-1">
                  Image <StarIcon />
                </span>
                {imagePreview ? (
                  <div className="relative rounded-xl overflow-hidden aspect-video w-full border border-border">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                    <span
                      onClick={handleImageRemove}
                      className="top-2 right-2 absolute z-10 bg-background border border-border rounded-lg p-1.5 cursor-pointer"
                    >
                      <DeleteIcon />
                    </span>
                  </div>
                ) : (
                  <Uploader
                    value={[]}
                    onValueChange={handleImageSelect}
                    maxFiles={1}
                    accept={{
                      "image/jpg": [],
                      "image/jpeg": [],
                      "image/png": [],
                      "image/gif": [],
                    }}
                    className="flex flex-col justify-center h-96 items-center gap-3 py-10 border border-dashed border-border rounded-xl cursor-pointer"
                  >
                    <div className="flex flex-col items-center gap-2 text-secondary">
                      <ImageIcon className="w-8 h-8 opacity-40" />
                      <span className="font-semibold text-sm">
                        Click to upload image
                      </span>
                      <span className="text-xs">PNG, JPG, GIF up to 2MB</span>
                    </div>
                  </Uploader>
                )}
              </div>

              {/* Column 2: Selectors */}
              <div className="flex flex-col gap-6">
                {/* Action type */}
                <FormSelect
                  name="actionType"
                  label="Action type"
                  placeholder="Select action type"
                  form={form}
                  required
                  options={actionTypeOptions}
                />

                {/* Link URL (only when action type is "link") */}
                {watchedActionType === CarouselActionType.LINK && (
                  <FormInput
                    name="url"
                    label="URL"
                    placeholder="https://example.com"
                    form={form}
                    required
                    type="text"
                  />
                )}

                {/* Navigation fields (only when action type is "navigation") */}
                {watchedActionType === CarouselActionType.NAVIGATION && (
                  <>
                    <FormSelect
                      name="navigationTarget"
                      label="Navigation target"
                      placeholder="Select screen"
                      form={form}
                      required
                      options={navigationTargetOptions}
                    />

                    {/* Campaign selector (only for campaign-screen) */}
                    {watchedNavigationTarget ===
                      NavigationTarget.CAMPAIGN_SCREEN && (
                      <FormSearch
                        form={form}
                        name="campaignId"
                        label="Campaign"
                        placeholder="Search campaign"
                        required
                        options={campaigns.map((c) => ({
                          value: c.id,
                          label: c.name,
                        }))}
                        onSearch={debouncedSearchCampaigns}
                        fetching={searchingCampaigns}
                        showAllOnEmpty
                        maxResultsWhileSearching={20}
                      />
                    )}
                  </>
                )}
              </div>
            </form>
          </Form>
        )}
      </div>
    </div>
  );
};
